import * as React from 'react';
import { StyleSheet, css } from 'aphrodite/no-important';
import { TYPOGRAPHY_SIZE } from 'mm-theme-configuration/dist/consts';
import { useTheme } from '../../theming/useTheme';
import { VrLineHeight } from '../../theming/verticalRhythm/verticalRhythm';
import { MEDIA_BREAKPOINTS } from '../../mediaQueries.const';
import { ErrorBoundary } from '../errorHandling/ErrorBoundary';
import { MandatoryAdditionalStyle, AdditionalStyleSheet } from '../components.utils';
import { getStyleBySize, SemanticTagThemeProps } from '../../typography/semanticTags/semanticTags.utils';

const componentConfigurationKey = 'articleParagraph';

interface ParagraphComponentDataProps {
  html: string
  dataId?: string
}

interface ParagraphComponentThemeProps extends SemanticTagThemeProps {
  linkColor: string
  vrLineHeight: VrLineHeight
}

type ParagraphComponentProps = ParagraphComponentDataProps & ParagraphComponentThemeProps & MandatoryAdditionalStyle;

const getStyle = (theme: ParagraphComponentThemeProps) => {
  const { linkColor, vrLineHeight } = theme;
  return (element: string) => {
    switch (element) {
      case 'a': return StyleSheet.create({
        style: {
          color: linkColor,
          textDecoration: 'none',
        },
      }).style;

      case 'ol':
      case 'ul': return StyleSheet.create({
        style: {
          listStylePosition: 'inside',
          padding: 0,
          [MEDIA_BREAKPOINTS.LARGE]: {
            margin: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE]}px 0`,
          },
          [MEDIA_BREAKPOINTS.MEDIUM]: {
            margin: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM]}px 0`,
          },
          [MEDIA_BREAKPOINTS.SMALL]: {
            margin: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL]}px 0`,
          },
        },
      }).style;

      default: return null;
    }
  };
};

const applyCssClasses = (html: string, theme: ParagraphComponentThemeProps, typographyStyle: { [key: string]: any }): string => {
  const style = getStyle(theme);
  return html
    .replace(/<p/g, `<p class="${css(typographyStyle)}"`)
    .replace(/<ol/g, `<ol class="${css(style('ol'), typographyStyle)}"`)
    .replace(/<ul/g, `<ul class="${css(style('ul'), typographyStyle)}"`)
    .replace(/<a/g, `<a class="${css(style('a'), typographyStyle)}"`);
};

const extractWrapper = (html: string) => {
  const regex = /^<(.*?)>/;
  const match = html.match(regex);
  if (!match) {
    return ['p', html];
  }

  const indexOfLastElement = html.lastIndexOf('<');
  return [
    match[1],
    html.substr(0, indexOfLastElement).replace(match[0], ''),
  ];
};

const getWrapperAsElement = (wrapper: string, content: string, style: { [key: string]: any } | null, additionalStyle: AdditionalStyleSheet, dataId:string | undefined, typographyStyle: { [key: string]: any }) => {
  return React.createElement(
    wrapper,
    {
      dangerouslySetInnerHTML: { __html: content },
      className: css(style, additionalStyle.style, typographyStyle),
      'data-id': dataId,
    },
  );
};

export const ParagraphComponent: React.FunctionComponent<ParagraphComponentProps> = props => {
  const { html, additionalStyle, dataId, fontSizesStyles } = props;
  const [wrapper, content] = extractWrapper(html);
  const wrapperStyle = getStyle(props)(wrapper);
  const typographyStyle = getStyleBySize(fontSizesStyles, TYPOGRAPHY_SIZE.NORMAL);

  return (
    getWrapperAsElement(wrapper, applyCssClasses(content, props, typographyStyle), wrapperStyle, additionalStyle, dataId, typographyStyle)
  );
};

const mapThemeProps = (theme: ParagraphComponentThemeProps) => ({
  linkColor: theme.linkColor,
  vrLineHeight: theme.vrLineHeight,
  fontSizesStyles: theme.fontSizesStyles,
});

export const Paragraph = (props: ParagraphComponentDataProps & MandatoryAdditionalStyle) => {
  const themeContext = useTheme(mapThemeProps, componentConfigurationKey);
  return <ErrorBoundary><ParagraphComponent {...themeContext} {...props} /></ErrorBoundary>;
};
